import QRCode from 'qrcode';
import { useMemo } from 'react';
import { DEFAULT_QR_STYLE, type QrStyle } from '@/lib/qrStyles';

interface QRCodeSVGProps {
  value: string;
  /** Rendered size in CSS pixels (the SVG itself is resolution independent). */
  size?: number;
  level?: 'L' | 'M' | 'Q' | 'H';
  className?: string;
  /** Color of the QR modules (foreground). */
  fgColor?: string;
  /** Color behind the modules, including the quiet zone. */
  bgColor?: string;
  /** Module shape. See `@/lib/qrStyles`. */
  style?: QrStyle;
  /** Quiet zone width, in modules. */
  margin?: number;
}

/** Radii (in module units) used for the three finder "eyes" of each style. */
const FINDER_RADII: Record<QrStyle, { outer: number; inner: number }> = {
  square: { outer: 0, inner: 0 },
  rounded: { outer: 2, inner: 0.9 },
  dots: { outer: 3.5, inner: 1.5 },
  lines: { outer: 1.6, inner: 0.8 },
  columns: { outer: 1.6, inner: 0.8 },
  diamond: { outer: 1.2, inner: 0.6 },
};

/** Trim float noise so the generated path data stays small. */
function n(value: number): string {
  return Number(value.toFixed(3)).toString();
}

function rectPath(x: number, y: number, w: number, h: number, radius: number): string {
  const r = Math.min(radius, w / 2, h / 2);
  if (r <= 0) {
    return `M${n(x)},${n(y)}h${n(w)}v${n(h)}h${n(-w)}z`;
  }
  return (
    `M${n(x + r)},${n(y)}` +
    `h${n(w - 2 * r)}a${n(r)},${n(r)} 0 0 1 ${n(r)},${n(r)}` +
    `v${n(h - 2 * r)}a${n(r)},${n(r)} 0 0 1 ${n(-r)},${n(r)}` +
    `h${n(-(w - 2 * r))}a${n(r)},${n(r)} 0 0 1 ${n(-r)},${n(-r)}` +
    `v${n(-(h - 2 * r))}a${n(r)},${n(r)} 0 0 1 ${n(r)},${n(-r)}z`
  );
}

function circlePath(cx: number, cy: number, r: number): string {
  return (
    `M${n(cx - r)},${n(cy)}` +
    `a${n(r)},${n(r)} 0 1 0 ${n(r * 2)},0` +
    `a${n(r)},${n(r)} 0 1 0 ${n(-r * 2)},0z`
  );
}

function diamondPath(x: number, y: number): string {
  const c = 0.5;
  const half = 0.56;
  return (
    `M${n(x + c)},${n(y + c - half)}` +
    `L${n(x + c + half)},${n(y + c)}` +
    `L${n(x + c)},${n(y + c + half)}` +
    `L${n(x + c - half)},${n(y + c)}z`
  );
}

/**
 * A QR code rendered as SVG, with selectable module shapes (dots, lines,
 * rounded, etc.). The finder patterns are always drawn solid and the quiet
 * zone is preserved, so every style scans.
 */
export function QRCodeSVG({
  value,
  size = 256,
  level = 'Q',
  className,
  fgColor = '#000000',
  bgColor = '#ffffff',
  style = DEFAULT_QR_STYLE,
  margin = 2,
}: QRCodeSVGProps) {
  const model = useMemo(() => {
    if (!value) return null;

    let qr: ReturnType<typeof QRCode.create>;
    try {
      qr = QRCode.create(value, { errorCorrectionLevel: level });
    } catch (error) {
      console.error('QR Code generation error:', error);
      return null;
    }

    const count = qr.modules.size;
    const data = qr.modules.data;
    const total = count + margin * 2;

    // The three 7x7 finder patterns are drawn separately so they stay solid.
    const isFinder = (row: number, col: number) =>
      (row < 7 && col < 7) ||
      (row < 7 && col >= count - 7) ||
      (row >= count - 7 && col < 7);

    const dark = (row: number, col: number) =>
      row >= 0 && col >= 0 && row < count && col < count &&
      !!data[row * count + col] && !isFinder(row, col);

    const parts: string[] = [];

    if (style === 'lines' || style === 'columns') {
      // Merge consecutive modules into capsules along one axis.
      const horizontal = style === 'lines';
      for (let a = 0; a < count; a++) {
        let run = 0;
        for (let b = 0; b <= count; b++) {
          const on = b < count && (horizontal ? dark(a, b) : dark(b, a));
          if (on) {
            run++;
            continue;
          }
          if (run > 0) {
            const start = b - run;
            const x = margin + (horizontal ? start : a);
            const y = margin + (horizontal ? a : start);
            const w = horizontal ? run : 1;
            const h = horizontal ? 1 : run;
            parts.push(rectPath(x + 0.06, y + 0.06, w - 0.12, h - 0.12, 0.47));
            run = 0;
          }
        }
      }
    } else {
      for (let row = 0; row < count; row++) {
        for (let col = 0; col < count; col++) {
          if (!dark(row, col)) continue;
          const x = margin + col;
          const y = margin + row;
          switch (style) {
            case 'dots':
              parts.push(circlePath(x + 0.5, y + 0.5, 0.45));
              break;
            case 'diamond':
              parts.push(diamondPath(x, y));
              break;
            case 'rounded':
              parts.push(rectPath(x + 0.04, y + 0.04, 0.92, 0.92, 0.32));
              break;
            case 'square':
            default:
              parts.push(rectPath(x, y, 1, 1, 0));
              break;
          }
        }
      }
    }

    const radii = FINDER_RADII[style] ?? FINDER_RADII.square;
    const finders: { outer: string; gap: string; inner: string }[] = [
      [0, 0],
      [count - 7, 0],
      [0, count - 7],
    ].map(([col, row]) => {
      const x = margin + col;
      const y = margin + row;
      return {
        outer: rectPath(x, y, 7, 7, radii.outer),
        gap: rectPath(x + 1, y + 1, 5, 5, Math.max(radii.outer - 1, 0)),
        inner: rectPath(x + 2, y + 2, 3, 3, radii.inner),
      };
    });

    return { total, data: parts.join(''), finders };
  }, [value, level, margin, style]);

  if (!model) return null;

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox={`0 0 ${model.total} ${model.total}`}
      shapeRendering="geometricPrecision"
      role="img"
      aria-hidden="true"
    >
      <rect width={model.total} height={model.total} fill={bgColor} />
      <path d={model.data} fill={fgColor} />
      {model.finders.map((finder, i) => (
        <g key={i}>
          <path d={finder.outer} fill={fgColor} />
          <path d={finder.gap} fill={bgColor} />
          <path d={finder.inner} fill={fgColor} />
        </g>
      ))}
    </svg>
  );
}
